import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Skeleton,
  Alert,
  Pagination,
  SelectChangeEvent,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Stack,
  Grid,
  LinearProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  PlayCircle,
  CloudUpload,
  Close,
  VideoFile,
} from "@mui/icons-material";
import {
  videosAPI,
  CreateVideoPayload,
  UpdateVideoPayload,
  VideoCategory,
  MAX_VIDEO_UPLOAD_MB,
  MAX_VIDEO_UPLOAD_BYTES,
} from "../../services/adminApi";

interface Video {
  _id: string;
  title: string;
  description: string;
  cloudflareVideoId: string;
  playbackUrl: string;
  thumbnailUrl: string;
  duration: number;
  status: "processing" | "ready" | "error";
  visibility: "public" | "members" | "draft";
  level?: "none" | "pre-recorded" | "advanced";
  plan?: "free" | "basic" | "premium";
  isFree: boolean;
  category?: string;
  tags: string[];
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const ADVANCED_CATEGORIES = [
  "Total Face Flow",
  "Lower Face Flow",
  "Middle Face Flow",
  "Upper Face Flow",
  "Facial Massage",
  "Gua Sha Ritual",
  "Taping Technique",
  "Beyond the Face",
] as const;

const PRE_RECORDED_CATEGORIES = [
  "Activation",
  "Flow 1",
  "Flow 2",
  "Flow 3",
  "Flow 4",
  "Flow 5",
  "Release",
] as const;

const ALL_CATEGORIES = [...ADVANCED_CATEGORIES, ...PRE_RECORDED_CATEGORIES] as const;

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  ready: { bg: "#ECFDF5", color: "#10B981" },
  processing: { bg: "#FFF7ED", color: "#F59E0B" },
  error: { bg: "#FEF2F2", color: "#EF4444" },
};

const VISIBILITY_STYLES: Record<string, { bg: string; color: string }> = {
  public: { bg: "#EFF6FF", color: "#3B82F6" },
  members: { bg: "rgba(21,101,192,0.08)", color: "#1565C0" },
  draft: { bg: "rgba(203, 184, 168, 0.15)", color: "#6B7280" },
};

const LEVEL_STYLES: Record<string, { bg: string; color: string }> = {
  none: { bg: "#F3F4F6", color: "#6B7280" },
  "pre-recorded": { bg: "#F0FDF4", color: "#16A34A" },
  advanced: { bg: "#FEF3C7", color: "#B45309" },
};

const AdminVideos: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [freeFilter, setFreeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    sourceUrl: "",
    visibility: "draft" as string,
    level: "none" as "none" | "pre-recorded" | "advanced",
    plan: "free" as "free" | "basic" | "premium",
    isFree: false,
    category: "",
    tags: "",
  });

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Preview dialog
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadVideos = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params: any = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (visibilityFilter) params.visibility = visibilityFilter;
      if (levelFilter) params.level = levelFilter;
      if (freeFilter) params.isFree = freeFilter === "free";
      if (categoryFilter) params.category = categoryFilter;

      const res = await videosAPI.list(params);
      setVideos(
        (res.data.videos || []).map((video: any) => ({
          ...video,
          level: video.level || "none",
          isFree: !!video.isFree,
        }))
      );
      setPagination(res.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load videos");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter, visibilityFilter, levelFilter, freeFilter, categoryFilter]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // â”€â”€â”€ Create / Edit Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const openCreateDialog = () => {
    setDialogMode("create");
    setSelectedVideo(null);
    setFormData({
      title: "",
      description: "",
      sourceUrl: "",
      visibility: "draft",
      level: "none",
      plan: "free",
      isFree: false,
      category: "",
      tags: "",
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setDialogOpen(true);
  };

  const openEditDialog = (video: Video) => {
    setDialogMode("edit");
    setSelectedVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      sourceUrl: "",
      visibility: video.visibility,
      level: (video.level || "none") as "none" | "pre-recorded" | "advanced",
      plan: video.plan || "free",
      isFree: !!video.isFree,
      category: video.category || "",
      tags: video.tags.join(", "),
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setDialogOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
      if (!validTypes.includes(file.type)) {
        setError("Please select a valid video file (MP4, WebM, MOV, or AVI)");
        return;
      }
      // Same ceiling the API enforces, so the limit is stated once.
      if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
        setError(`Video file must be smaller than ${MAX_VIDEO_UPLOAD_MB} MB`);
        return;
      }
      setSelectedFile(file);
      // Auto-fill title from filename if empty
      if (!formData.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        setFormData((prev) => ({ ...prev, title: nameWithoutExt }));
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      
      if (dialogMode === "create") {
        if (selectedFile) {
          // Upload file to Cloudflare Stream
          setUploading(true);
          try {
            const { videoId } = await videosAPI.uploadFile(
              selectedFile,
              formData.title,
              (progress) => setUploadProgress(progress)
            );
            
            // Update video with metadata after upload
            const payload: UpdateVideoPayload = {
              title: formData.title,
              description: formData.description,
              visibility: formData.visibility,
              level: formData.level,
              isFree: formData.isFree,
              plan: formData.plan,
              category:(formData.category as VideoCategory),
              tags: formData.tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean),
            };
            await videosAPI.update(videoId, payload);
            
            // Confirm upload to check status
            await videosAPI.confirmUpload(videoId);
          } finally {
            setUploading(false);
          }
        } else {
          // Create with source URL (or simulated)
          const payload: CreateVideoPayload = {
            title: formData.title,
            description: formData.description,
            sourceUrl: formData.sourceUrl,
            visibility: formData.visibility,
            level: formData.level,
            isFree: formData.isFree,
            plan: formData.plan,
            category: formData.category ? (formData.category as VideoCategory) : undefined,
            tags: formData.tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean),
          };
          await videosAPI.create(payload);
        }
      } else if (selectedVideo) {
        const payload: UpdateVideoPayload = {
          title: formData.title,
          description: formData.description,
          visibility: formData.visibility,
          level: formData.level,
          isFree: formData.isFree,
          plan: formData.plan,
          category:(formData.category as VideoCategory),
          tags: formData.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
        };
        await videosAPI.update(selectedVideo._id, payload);
      }
      setDialogOpen(false);
      loadVideos();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to save video");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  // â”€â”€â”€ Visibility Toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleVisibilityChange = async (
    video: Video,
    newVisibility: string
  ) => {
    try {
      await videosAPI.updateVisibility(video._id, newVisibility);
      loadVideos();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update visibility");
    }
  };

  // â”€â”€â”€ Delete â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const openDeleteDialog = (video: Video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!videoToDelete) return;
    try {
      setDeleting(true);
      await videosAPI.delete(videoToDelete._id);
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
      loadVideos();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete video");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
          Video Management
        </Typography>
        <Button
          variant="contained"
          startIcon={!isMobile && <Add />}
          onClick={openCreateDialog}
          sx={{
            bgcolor: "#1565C0",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.2 },
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            "&:hover": { bgcolor: "#BFA78F" },
          }}
        >
          {isMobile ? <Add /> : "Add Video"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: 2,
          display: "flex",
          gap: { xs: 1.5, sm: 2 },
          flexWrap: "wrap",
          alignItems: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          border: "1px solid rgba(0,0,0,0.04)",
        }}
      >
        <TextField
          placeholder="Search videos..."
          size="small"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 140 } }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e: SelectChangeEvent) => {
              setStatusFilter(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ready">Ready</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 140 } }}>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={visibilityFilter}
            label="Visibility"
            onChange={(e: SelectChangeEvent) => {
              setVisibilityFilter(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="members">Members</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 140 } }}>
          <InputLabel>Access</InputLabel>
          <Select
            value={freeFilter}
            label="Access"
            onChange={(e: SelectChangeEvent) => {
              setFreeFilter(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 140 } }}>
          <InputLabel>Level</InputLabel>
          <Select
            value={levelFilter}
            label="Level"
            onChange={(e: SelectChangeEvent) => { setLevelFilter(e.target.value); setCategoryFilter(""); setPagination((prev) => ({ ...prev, page: 1 })); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="pre-recorded">Pre-Recorded</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 180 } }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e: SelectChangeEvent) => {
              setCategoryFilter(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <MenuItem value="">All</MenuItem>
            {(levelFilter === "pre-recorded"
              ? PRE_RECORDED_CATEGORIES
              : levelFilter === "advanced"
              ? ADVANCED_CATEGORIES
              : ALL_CATEGORIES
            ).map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Mobile Card View */}
      {isMobile ? (
        <Box>
          {loading ? (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid size={{ xs: 12, sm: 6 }} key={i}>
                  <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : videos.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              <CloudUpload sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">
                No videos found. Tap "+" to add one.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {videos.map((video) => (
                <Grid size={{ xs: 12, sm: 6 }} key={video._id}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.04)",
                    }}
                  >
                    <Box
                      sx={{
                        height: 120,
                        bgcolor: "rgba(203, 184, 168, 0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <PlayCircle sx={{ fontSize: 48, color: "#CBB8A8" }} />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.7)",
                          color: "white",
                          px: 1,
                          py: 0.3,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {formatDuration(video.duration)}
                      </Box>
                    </Box>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }} noWrap>
                        {video.title}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          label={video.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                            bgcolor: STATUS_STYLES[video.status]?.bg,
                            color: STATUS_STYLES[video.status]?.color,
                          }}
                        />
                        <Chip
                          label={video.visibility}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                            bgcolor: VISIBILITY_STYLES[video.visibility]?.bg,
                            color: VISIBILITY_STYLES[video.visibility]?.color,
                          }}
                        />
                        <Chip
                          label={video.isFree ? "Free" : "Pro"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                            bgcolor: video.isFree ? "#DCFCE7" : "#F3F4F6",
                            color: video.isFree ? "#15803D" : "#6B7280",
                          }}
                        />
                        {video.tags?.includes("ff_onboarding") && (
                          <Chip
                            label="ðŸŒ¿ Onboarding"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.65rem",
                              height: 22,
                              bgcolor: "rgba(216,154,122,0.15)",
                              color: "#8C654F",
                              border: "1px solid rgba(216,154,122,0.4)",
                            }}
                          />
                        )}
                        <Chip
                          label={video.plan || "free"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                            textTransform: "capitalize",
                            bgcolor:
                              (video.plan || "free") === "premium"
                                ? "rgba(216,154,122,0.15)"
                                : (video.plan || "free") === "basic"
                                ? "#EFF6FF"
                                : "#F3F4F6",
                            color:
                              (video.plan || "free") === "premium"
                                ? "#1565C0"
                                : (video.plan || "free") === "basic"
                                ? "#3B82F6"
                                : "#6B7280",
                          }}
                        />
                        <Chip
                          label={video.level || "none"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                            textTransform: "capitalize",
                            bgcolor: LEVEL_STYLES[video.level || "none"]?.bg,
                            color: LEVEL_STYLES[video.level || "none"]?.color,
                          }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(video.createdAt)}
                      </Typography>
                      {video.category && (
                        <Chip
                          label={video.category}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 22,
                            bgcolor: "rgba(216,154,122,0.12)",
                            color: "#B45309",
                          }}
                        />
                      )}
                    </CardContent>
                    <CardActions sx={{ pt: 0, px: 2, pb: 1.5, justifyContent: "flex-end" }}>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(video)}
                        sx={{ color: "#1565C0" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(video)}
                        sx={{ color: "#EF4444" }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
      /* Desktop Table */
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          border: "1px solid rgba(0,0,0,0.04)",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#FAF7F4" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                THUMBNAIL
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                TITLE
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                DURATION
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                STATUS
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                VISIBILITY
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                ACCESS
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                PLAN
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                LEVEL
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                CATEGORY
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}>
                CREATED
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#6B7280" }}
              >
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(11)].map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : videos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                  <CloudUpload
                    sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    No videos found. Click "Add Video" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              videos.map((video) => (
                <TableRow
                  key={video._id}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  {/* Thumbnail */}
                  <TableCell>
                    <Box
                      onClick={() => video.playbackUrl && setPreviewVideo(video)}
                      sx={{
                        width: 80,
                        height: 45,
                        borderRadius: 1,
                        bgcolor: "rgba(203, 184, 168, 0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        cursor: video.playbackUrl ? "pointer" : "default",
                        position: "relative",
                        "&:hover .play-icon": { opacity: 1 },
                      }}
                    >
                      {video.thumbnailUrl ? (
                        <>
                          <Box
                            component="img"
                            src={video.thumbnailUrl}
                            alt={video.title}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e: any) => { e.target.style.display = "none"; }}
                          />
                          <Box
                            className="play-icon"
                            sx={{
                              position: "absolute", inset: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              bgcolor: "rgba(0,0,0,0.35)", opacity: 0, transition: "opacity 0.2s",
                            }}
                          >
                            <PlayCircle sx={{ color: "#fff", fontSize: 24 }} />
                          </Box>
                        </>
                      ) : (
                        <PlayCircle sx={{ color: "#CBB8A8" }} />
                      )}
                    </Box>
                  </TableCell>
                  {/* Title */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, maxWidth: 250 }}
                      noWrap
                    >
                      {video.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {video.cloudflareVideoId.substring(0, 12)}...
                    </Typography>
                  </TableCell>
                  {/* Duration */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDuration(video.duration)}
                    </Typography>
                  </TableCell>
                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={video.status.charAt(0).toUpperCase() + video.status.slice(1)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        bgcolor: STATUS_STYLES[video.status]?.bg,
                        color: STATUS_STYLES[video.status]?.color,
                      }}
                    />
                  </TableCell>
                  {/* Visibility */}
                  <TableCell>
                    <FormControl size="small" variant="standard">
                      <Select
                        value={video.visibility}
                        onChange={(e: SelectChangeEvent) =>
                          handleVisibilityChange(video, e.target.value)
                        }
                        disableUnderline
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: VISIBILITY_STYLES[video.visibility]?.color,
                          "& .MuiSelect-select": { py: 0.5, px: 1 },
                        }}
                      >
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="members">Members</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={video.isFree ? "Free" : "Pro"}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: video.isFree ? "#DCFCE7" : "#F3F4F6",
                        color: video.isFree ? "#15803D" : "#6B7280",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={video.plan || "free"}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 600,
                        bgcolor:
                          (video.plan || "free") === "premium"
                            ? "rgba(216,154,122,0.15)"
                            : (video.plan || "free") === "basic"
                            ? "#EFF6FF"
                            : "#F3F4F6",
                        color:
                          (video.plan || "free") === "premium"
                            ? "#1565C0"
                            : (video.plan || "free") === "basic"
                            ? "#3B82F6"
                            : "#6B7280",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={video.level || "none"}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 600,
                        bgcolor: LEVEL_STYLES[video.level || "none"]?.bg,
                        color: LEVEL_STYLES[video.level || "none"]?.color,
                      }}
                    />
                  </TableCell>
                  {/* Category */}
                  <TableCell>
                    {video.category ? (
                      <Chip
                        label={video.category}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          bgcolor: "rgba(216,154,122,0.12)",
                          color: "#B45309",
                          maxWidth: 140,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: "#D1D5DB" }}>â€”</Typography>
                    )}
                  </TableCell>
                  {/* Created */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {formatDate(video.createdAt)}
                    </Typography>
                  </TableCell>
                  {/* Actions */}
                  <TableCell align="right">
                    {video.playbackUrl && (
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => setPreviewVideo(video)}
                          sx={{ color: "#6B7280" }}
                        >
                          <PlayCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(video)}
                        sx={{ color: "#1565C0" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(video)}
                        sx={{ color: "#EF4444" }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 2, sm: 3 } }}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={(_, page) =>
              setPagination((prev) => ({ ...prev, page }))
            }
            color="primary"
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {dialogMode === "create" ? "Add New Video" : "Edit Video"}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            {/* File Upload Section - Only in create mode */}
            {dialogMode === "create" && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Video File
                </Typography>
                <Box
                  sx={{
                    border: selectedFile ? "2px solid #1565C0" : "2px dashed #E0E0E0",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    bgcolor: selectedFile ? "rgba(216, 154, 122, 0.04)" : "transparent",
                    "&:hover": {
                      borderColor: "#1565C0",
                      bgcolor: "rgba(216, 154, 122, 0.04)",
                    },
                  }}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  {selectedFile ? (
                    <Box>
                      <VideoFile sx={{ fontSize: 48, color: "#1565C0", mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </Typography>
                      {!uploading && (
                        <Button
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile();
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <CloudUpload sx={{ fontSize: 48, color: "#CBB8A8", mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Click to upload video
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        MP4, WebM, MOV, AVI (max {MAX_VIDEO_UPLOAD_MB} MB)
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Upload Progress */}
                {uploading && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Uploading to Cloudflare Stream...
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {uploadProgress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "rgba(203, 184, 168, 0.25)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#1565C0",
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                )}
                
                {/* OR divider */}
                {!selectedFile && (
                  <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
                    <Box sx={{ flex: 1, height: 1, bgcolor: "rgba(203, 184, 168, 0.25)" }} />
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                      OR
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: "rgba(203, 184, 168, 0.25)" }} />
                  </Box>
                )}
              </Box>
            )}

            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              disabled={uploading}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={uploading}
            />
            {dialogMode === "create" && !selectedFile && (
              <TextField
                label="Source URL (optional)"
                fullWidth
                placeholder="https://example.com/video.mp4"
                value={formData.sourceUrl}
                onChange={(e) =>
                  setFormData({ ...formData, sourceUrl: e.target.value })
                }
                helperText="Direct link to video file. Leave empty for simulated upload."
                disabled={uploading}
              />
            )}
            <FormControl fullWidth disabled={uploading}>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility}
                label="Visibility"
                onChange={(e: SelectChangeEvent) =>
                  setFormData({ ...formData, visibility: e.target.value })
                }
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="members">Members Only</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isFree}
                  onChange={(e) =>
                    setFormData({ ...formData, isFree: e.target.checked })
                  }
                  disabled={uploading}
                  color="success"
                />
              }
              label={formData.isFree ? "Free video" : "Pro (members) video"}
            />

            <FormControl fullWidth disabled={uploading}>
              <InputLabel>Plan</InputLabel>
              <Select
                value={formData.plan}
                label="Plan"
                onChange={(e: SelectChangeEvent) =>
                  setFormData({ ...formData, plan: e.target.value as "free" | "basic" | "premium" })
                }
              >
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={uploading}>
              <InputLabel>Level</InputLabel>
              <Select
                value={formData.level}
                label="Level"
                onChange={(e: SelectChangeEvent) =>
                  setFormData({ ...formData, level: e.target.value as "none" | "pre-recorded" | "advanced", category: "" })
                }
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="pre-recorded">Pre-Recorded (Foundation)</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
            {(formData.level === "advanced" || formData.level === "pre-recorded") && (
              <FormControl fullWidth disabled={uploading}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e: SelectChangeEvent) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <MenuItem value="">â€” None â€”</MenuItem>
                  {(formData.level === "pre-recorded"
                    ? PRE_RECORDED_CATEGORIES
                    : ADVANCED_CATEGORIES
                  ).map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Additional Tags"
              fullWidth
              placeholder="yoga, skin, beginner (comma separated)"
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
              disabled={uploading}
            />

            {/* Onboarding toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: formData.tags.split(",").map((t) => t.trim()).includes("ff_onboarding")
                  ? "#1565C0"
                  : "rgba(0,0,0,0.12)",
                backgroundColor: formData.tags.split(",").map((t) => t.trim()).includes("ff_onboarding")
                  ? "rgba(216,154,122,0.06)"
                  : "transparent",
                transition: "all 0.2s ease",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#2D2D2D" }}>
                  Show in new member onboarding popup
                </Typography>
                <Typography variant="caption" sx={{ color: "#78909C" }}>
                  This video will appear when a new member signs up for the first time
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.tags.split(",").map((t) => t.trim()).includes("ff_onboarding")}
                    onChange={(e) => {
                      const currentTags = formData.tags
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean)
                        .filter((t) => t !== "ff_onboarding");
                      if (e.target.checked) currentTags.push("ff_onboarding");
                      setFormData({ ...formData, tags: currentTags.join(", ") });
                    }}
                    disabled={uploading}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#1565C0" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#1565C0" },
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: "none" }}
            disabled={uploading || saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || uploading || !formData.title}
            sx={{
              bgcolor: "#1565C0",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { bgcolor: "#BFA78F" },
            }}
          >
            {uploading
              ? `Uploading... ${uploadProgress}%`
              : saving
              ? "Saving..."
              : dialogMode === "create"
              ? selectedFile
                ? "Upload & Save"
                : "Create Video"
              : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Video</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{videoToDelete?.title}</strong>? This will also remove the
            video from Cloudflare Stream. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Preview Dialog */}
      <Dialog
        open={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: "#000" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#fff",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff" }} noWrap>
            {previewVideo?.title}
          </Typography>
          <IconButton onClick={() => setPreviewVideo(null)} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewVideo?.playbackUrl && (
            <video
              src={previewVideo.playbackUrl}
              controls
              autoPlay
              style={{ width: "100%", maxHeight: "70vh", display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminVideos;


